import { useNavigate, useParams } from 'react-router-dom';
import { useSessions } from '@/api/queries';
import { SessionListSkeleton } from '@/components/feedback/Skeletons';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { relativeTime } from '@/lib/format';
import { SessionDrawer } from '@/components/session/SessionDrawer';

export function SessionsPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: sessions, isLoading } = useSessions('open');

  function openSession(sessionId: string) {
    navigate(`/sessions/${encodeURIComponent(sessionId)}`);
  }

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Active sessions</h1>
        <p className="text-sm text-muted-foreground">
          {sessions ? `${sessions.length} open thread${sessions.length === 1 ? '' : 's'}` : 'Loading…'}
        </p>
      </header>

      {isLoading ? (
        <SessionListSkeleton />
      ) : sessions?.length === 0 ? (
        <div className="rounded-lg border border-dashed py-16 text-center text-sm text-muted-foreground">
          No open sessions.
        </div>
      ) : (
        <div className="grid gap-3">
          {sessions?.map((s) => (
            <Card
              key={s.session_id}
              className="cursor-pointer transition-colors hover:bg-accent/50"
              role="button"
              tabIndex={0}
              onClick={() => openSession(s.session_id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') openSession(s.session_id);
              }}
            >
              <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm">
                  {s.agent_name ?? '—'}{' '}
                  <span className="text-muted-foreground">↔</span> {s.human_name}
                </CardTitle>
                <Badge variant={s.initiated_by_agent ? 'secondary' : 'outline'}>
                  {s.initiated_by_agent ? 'agent-initiated' : 'human-initiated'}
                </Badge>
              </CardHeader>
              <CardContent className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  Channel <code>{s.channel_id}</code> · thread <code>{s.thread_ts}</code>
                </span>
                <span>Updated {relativeTime(s.updated_at)}</span>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {id && (
        <SessionDrawer sessionId={id} onClose={() => navigate('/sessions')} />
      )}
    </div>
  );
}
